import { useState, type ReactNode } from 'react';
import { Icon } from './Icon.js';
import { IconButton } from './IconButton.js';
import './Assistant.css';

export interface AssistantMessage {
  id: string;
  from: 'user' | 'assistant';
  body: ReactNode;
}

export interface AssistantProps {
  /** Standing intro, shown above the transcript. */
  intro?: ReactNode;
  /** Transcript. Empty on first open, which is the prototype's initial state. */
  messages?: AssistantMessage[];
  /**
   * Screen-aware starter questions. The prototype's are specific to the current
   * screen ("Why did pair 8818 score 0.86?") — generic prompts would defeat the
   * point, so the caller supplies them per route alongside the help topic.
   */
  suggestions?: string[];
  /**
   * Called with the question. **Required**, and there is no default: an assistant
   * that swallows input is worse than one that is visibly unbuilt.
   */
  onSubmit: (question: string) => void;
  placeholder?: string;
  /**
   * The line under the composer. Defaults to stating what the assistant can see,
   * because for a product holding a customer's CRM data "what does it read" is the
   * first question a reviewer asks.
   */
  disclosure?: ReactNode;
  /**
   * Slot for a bot-verification widget, rendered between the composer and the
   * disclosure.
   *
   * A slot rather than a built-in, because this component is presentation and the
   * verification provider is not its concern — `packages/ui` deliberately performs
   * no network access. The caller (`app-kit`'s help dock) owns the widget and the
   * token.
   *
   * Rendered here rather than in the shell so it mounts only when the assistant tab
   * is actually open: an invisible widget that loads on every page view is both a
   * request nobody asked for and a challenge nobody can complete.
   */
  verification?: ReactNode;
  busy?: boolean;
}

/**
 * "Ask Dedupr" — the in-app assistant surface (design-system catalogue, Tier 2;
 * F29's `Should [P2]` item).
 *
 * The prototype builds this as the second tab of the help panel, and the build had
 * neither the tab nor the surface (audit S-8). This is the **surface only**: F29's
 * open question about whether it is LLM-backed through M9's gateway — and so bound
 * by BR-8 data minimisation and F16 receipts — or a contextual-help search is
 * unresolved, so `onSubmit` hands the question to the app rather than this
 * component inventing a backend or, worse, a canned answer.
 */
export function Assistant({
  intro,
  messages = [],
  suggestions = [],
  onSubmit,
  placeholder = 'Ask about your data or this screen',
  disclosure,
  verification,
  busy = false,
}: AssistantProps) {
  const [value, setValue] = useState('');

  const submit = (question: string) => {
    const trimmed = question.trim();
    if (!trimmed || busy) return;
    onSubmit(trimmed);
    setValue('');
  };

  return (
    <div className="dpr-assistant">
      {intro ? (
        <div className="dpr-assistant__intro">
          <span className="dpr-assistant__intro-label">Dedupr assistant</span>
          <p className="dpr-assistant__intro-body">{intro}</p>
        </div>
      ) : null}

      {messages.length > 0 ? (
        // `log` rather than `alert`: replies arrive as additions to a
        // conversation, and an assertive live region would interrupt whatever the
        // user is reading on the screen behind the panel.
        <div className="dpr-assistant__thread" role="log" aria-live="polite" aria-label="Conversation">
          {messages.map((m) => (
            <div
              key={m.id}
              className={`dpr-assistant__message dpr-assistant__message--${m.from}`}
            >
              {m.body}
            </div>
          ))}
        </div>
      ) : (
        <div className="dpr-assistant__thread" />
      )}

      {suggestions.length > 0 ? (
        <div className="dpr-assistant__suggestions">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              className="dpr-assistant__suggestion"
              onClick={() => submit(s)}
            >
              {s}
            </button>
          ))}
        </div>
      ) : null}

      <form
        className="dpr-assistant__composer"
        onSubmit={(e) => {
          e.preventDefault();
          submit(value);
        }}
      >
        <input
          className="dpr-assistant__input"
          value={value}
          placeholder={placeholder}
          aria-label={placeholder}
          onChange={(e) => setValue(e.currentTarget.value)}
        />
        <IconButton
          type="submit"
          size="sm"
          variant="primary"
          icon={<Icon name="chevronRight" size={16} />}
          label="Send question"
          disabled={busy || value.trim().length === 0}
        />
      </form>

      {verification ? <div className="dpr-assistant__verification">{verification}</div> : null}

      <p className="dpr-assistant__disclosure">
        {disclosure ??
          'The assistant can read your findings and explain them. It never applies a change — anything it proposes still goes through the normal approval.'}
      </p>
    </div>
  );
}
